import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  TranscriptDateCellEditorParams,
  TranscriptDateCellEditorRef,
} from '$/components/transcriptDateCellEditor/TranscriptDateCellEditor.types';
import './TranscriptDateCellEditor.scss';

dayjs.extend(customParseFormat);

const TRANSCRIPT_DATE_INPUT_FORMAT = 'MM/DD/YYYY HH:mm';
const TRANSCRIPT_DATE_PICKER_FORMAT = 'MM/dd/yyyy HH:mm';
const TRANSCRIPT_DATE_PORTAL_ID = 'transcript-date-picker-portal';

const TranscriptDateEditorInput = forwardRef<HTMLInputElement, React.ComponentPropsWithoutRef<'input'>>(
  (props, ref) => {
    const { className, onKeyDown, onMouseDown, onClick, ...rest } = props;
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, []);

    return (
      <input
        {...rest}
        ref={node => {
          inputRef.current = node;

          if (!ref) return;
          if (typeof ref === 'function') {
            ref(node);
            return;
          }

          ref.current = node;
        }}
        className={`transcript-date-editor__input ${className ?? ''}`.trim()}
        onMouseDown={event => {
          event.stopPropagation();
          onMouseDown?.(event);
        }}
        onClick={event => {
          event.stopPropagation();
          onClick?.(event);
        }}
        onKeyDown={event => {
          event.stopPropagation();
          onKeyDown?.(event);
        }}
        placeholder={TRANSCRIPT_DATE_INPUT_FORMAT}
      />
    );
  }
);

TranscriptDateEditorInput.displayName = 'TranscriptDateEditorInput';

const TranscriptDateCellEditor = forwardRef<TranscriptDateCellEditorRef, TranscriptDateCellEditorParams>(
  (params, ref) => {
    const [value, setValue] = useState<Date | null>(params.value ? new Date(params.value) : new Date());
    const valueRef = React.useRef<Date | null>(value);
    const hasCommittedRef = React.useRef(false);
    const commitAndCloseRef = React.useRef<() => void>(() => undefined);
    const editorRef = React.useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = useState(value ? dayjs(value).format(TRANSCRIPT_DATE_INPUT_FORMAT) : '');

    useImperativeHandle(ref, () => ({
      getValue: () => valueRef.current,
    }));

    const handleChange = (date: Date | null) => {
      valueRef.current = date;
      setValue(date);
      setInputValue(date ? dayjs(date).format(TRANSCRIPT_DATE_INPUT_FORMAT) : '');
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setInputValue(nextValue);

      const parsedDate = dayjs(nextValue, TRANSCRIPT_DATE_INPUT_FORMAT, true);
      if (!parsedDate.isValid()) return;

      const nextDate = parsedDate.toDate();
      valueRef.current = nextDate;
      setValue(nextDate);
    };

    commitAndCloseRef.current = () => {
      if (hasCommittedRef.current) return;
      hasCommittedRef.current = true;

      if (valueRef.current && params.onSave) params.onSave(valueRef.current);
      params.stopEditing();
    };

    useEffect(() => {
      const handleMouseDown = (event: MouseEvent) => {
        const targetNode = event.target as Node | null;
        const portalRoot = document.getElementById(TRANSCRIPT_DATE_PORTAL_ID);

        if (editorRef.current?.contains(targetNode)) return;
        if (portalRoot?.contains(targetNode)) return;

        commitAndCloseRef.current();
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        event.stopPropagation();
        commitAndCloseRef.current();
      };

      document.addEventListener('mousedown', handleMouseDown, true);
      document.addEventListener('keydown', handleKeyDown, true);

      return () => {
        document.removeEventListener('mousedown', handleMouseDown, true);
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }, []);

    return (
      <div
        ref={editorRef}
        className="transcript-date-editor"
        onMouseDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}>
        <DatePicker
          selected={value}
          value={inputValue}
          onChange={handleChange}
          onChangeRaw={event => handleInputChange(event as React.ChangeEvent<HTMLInputElement>)}
          customInput={<TranscriptDateEditorInput />}
          wrapperClassName="transcript-date-editor__wrapper"
          open
          shouldCloseOnSelect={false}
          showTimeSelect
          timeIntervals={15}
          dateFormat={TRANSCRIPT_DATE_PICKER_FORMAT}
          popperPlacement="bottom-end"
          popperClassName="transcript-date-editor__popper"
          portalId={TRANSCRIPT_DATE_PORTAL_ID}
          popperModifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-end', 'top-end', 'bottom-start', 'top-start'],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                padding: 8,
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ]}
        />
      </div>
    );
  }
);

TranscriptDateCellEditor.displayName = 'TranscriptDateCellEditor';

export default TranscriptDateCellEditor;
